// src/app/modules/events/EventManagement.jsx
import React, {useEffect, useMemo, useRef, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    fetchEventsRequest,
    createEventRequest,
    updateEventRequest,
    deleteEventRequest,
    cancelEventRequest,
    replanifyEventRequest,
    duplicateEventRequest,
    exportBudgetRequest,
    exportFormationsRequest,
    importEventsExcelRequest,
    exportEventsExcelRequest,
    downloadEventsTemplateRequest,
} from "../../../../../redux/actions/eventsActions";

import { fetchModulesCleRequest }        from "../../../../../redux/actions/modulesCleActions";
import { fetchBudgetByCampagneRequest }  from "../../../../../redux/actions/campagnesActions";
import { fetchSallesRequest }            from "../../../../../redux/actions/sallesActions";
import { fetchConsultantsRequest }       from "../../../../../redux/actions/consultantsActions";
import { getAllEvaluationsRequest }      from "../../../../../redux/actions/evaluationActions";
import "../../RessourcesStyles/EventManagement.css";
import { Pencil, CalendarClock, Copy, Ban, Trash2, Download,Upload, X, Eye, CheckCircle, FileSpreadsheet } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const norm       = (v) => String(v || "").trim().toLowerCase();
const toIsoLocal = (dt) => (dt ? String(dt).slice(0, 16) : "");
const pf         = (v)  => parseFloat(v) || 0;

const toDecimalOrNull = (s) => {
    if (!s && s !== 0) return null;
    const n = Number(String(s).trim().replace(",", "."));
    return Number.isNaN(n) ? null : String(n);
};
const toIntOrNull = (s) => {
    if (s == null || s === "") return null;
    const n = Number(String(s).trim());
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

const statusColor = (s) => ({
    PLANIFIE:  { bg: "#e8edf7", color: "#204385" },
    EN_COURS:  { bg: "#d1fae5", color: "#059669" },
    TERMINE:   { bg: "#f3f4f6", color: "#6b7280" },
    ANNULE:    { bg: "#fde8e7", color: "#ee645f" },
}[s] || { bg: "#f3f4f6", color: "#6b7280" });

const emptyForm = {
    titre: "", description: "", moduleCleId: "",
    locationType: "ONSITE", salleId: "", offsiteLocation: "",
    dateDebut: "", dateFin: "",
    allowRegistration: true, registrationDeadlineHours: "",
    isPeriodic: false, maxPlaces: "", currency: "MAD",
};

const emptyBudget = () => ({
    tarifFormation: 0, repas: 0, transport: 0,
    hebergement: 0, salleConference: 0, autres: 0, autreDescription: "",
});

const emptyReplan = { dateDebut: "", dateFin: "" };

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal
// ─────────────────────────────────────────────────────────────────────────────

export default function EventManagement() {
    const dispatch = useDispatch();

    // ── Redux state ────────────────────────────────────────────────────────
    const eventsState      = useSelector((s) => s.events      || {});
    const modulesState     = useSelector((s) => s.modulesCle  || {});
    const sallesState      = useSelector((s) => s.salles      || {});
    const salles           = useSelector((s) => s.salles.salles);
    const consultantsState = useSelector((s) => s.consultants || {});
    const budgetState      = useSelector((s) => s.campagnes.budgets);
    const evaluationsState = useSelector((s) => s.evaluations || {});

    const evaluations = evaluationsState.evaluations || [];
    const events      = eventsState.events        || [];
    const modulesCle  = modulesState.modulesCle   || [];
    const consultants = consultantsState.consultants || [];



    const importResult = eventsState.importResult || null;

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [importAction,  setImportAction]  = useState("add");
    const [selectedFile,  setSelectedFile]  = useState(null);
    const [fileName,      setFileName]      = useState("");
    const fileInputRef = useRef(null); // + import useRef en haut

    useEffect(() => {
        if (importResult && !loading) setIsPreviewOpen(true);
    }, [importResult]);

// ── Handlers import ───────────────────────────────────────────────────────
    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        setSelectedFile(file);
        setFileName(file.name);
    };

    const clearFile = () => {
        setSelectedFile(null);
        setFileName("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleVerify = () => {
        if (!selectedFile) {
            Swal.fire({ icon: "warning", title: "Aucun fichier", text: "Sélectionnez un fichier Excel." });
            return;
        }
        dispatch(importEventsExcelRequest(selectedFile, true, importAction));
    };

    const handleConfirmImport = () => {
        setIsPreviewOpen(false);
        dispatch(importEventsExcelRequest(selectedFile, false, importAction));
        clearFile();
    };

    const campagneBudget  = budgetState || null;
    const montantCampagne = campagneBudget ? pf(campagneBudget.montant) : 0;
    const montantRestant  = campagneBudget
        ? (campagneBudget.budgetRestant != null
            ? pf(campagneBudget.budgetRestant)
            : pf(campagneBudget.montant))
        : 0;

    const loading = !!(eventsState.loading || modulesState.loading || sallesState.loading || consultantsState.loading);

    // ── Filtres ────────────────────────────────────────────────────────────
    const [tab, setTab] = useState("ALL");
    const [q,   setQ]   = useState("");

    // ── Modale CRUD ────────────────────────────────────────────────────────
    const [open,      setOpen]      = useState(false);
    const [modalTab,  setModalTab]  = useState("INFOS");
    const [isEdit,    setIsEdit]    = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form,      setForm]      = useState(emptyForm);

    // Consultants
    const [consultSearch,         setConsultSearch]         = useState("");
    const [selectedConsultantIds, setSelectedConsultantIds] = useState(new Set());

    // Sessions
    const [seancesCount, setSeancesCount] = useState(0);
    const [seances,      setSeances]      = useState([]);

    // Budget
    const [budgetType, setBudgetType] = useState("formation");
    const [budget,     setBudget]     = useState(emptyBudget());

    // Réplanification
    const [replanOpen,    setReplanOpen]    = useState(false);
    const [replanEventId, setReplanEventId] = useState(null);
    const [replanForm,    setReplanForm]    = useState(emptyReplan);

    useEffect(() => {
        dispatch(fetchEventsRequest());
        dispatch(fetchModulesCleRequest());
        dispatch(fetchSallesRequest());
        dispatch(fetchConsultantsRequest());
        dispatch(getAllEvaluationsRequest());
    }, [dispatch]);

    // ── Calculs budget ─────────────────────────────────────────────────────
    const totalCout = budgetType === "formation"
        ? pf(budget.tarifFormation)
        : pf(budget.repas) + pf(budget.transport) + pf(budget.hebergement)
        + pf(budget.salleConference) + pf(budget.autres);

    const budgetRestantCalc = montantRestant - totalCout;

    // ── évaluations séléctionnés ───────────────────────────────────────────────────

    const [selectedEvaluationIds, setSelectedEvaluationIds] = useState(new Set());
    const [evaluationSearch, setEvaluationSearch] = useState("");

    // ── Filtrage tableau ───────────────────────────────────────────────────
    const filteredEvents = useMemo(() => {
        const s = norm(q);
        return events.filter((e) => {
            const okTab    = tab === "ALL" || String(e.locationType) === tab;
            const okSearch = !s || norm(e.titre).includes(s) || norm(e.moduleCle && e.moduleCle.nom).includes(s)
                || norm(e.salle && e.salle.nom).includes(s) || norm(e.offsiteLocation).includes(s);
            return okTab && okSearch;
        });
    }, [events, tab, q]);

    const filteredConsultants = useMemo(() => {
        const s = norm(consultSearch);
        return consultants.filter((c) =>
            !s || norm(c.nomContact).includes(s) || norm(c.emailContact).includes(s) || norm(c.specialite).includes(s)
        );
    }, [consultants, consultSearch]);

    // ── Salles filtrées ────────────────────────────────────────────────────
    const sallesFiltrees = useMemo(() => {
        if (!salles) return [];
        if (form.locationType === "ONSITE")    return salles.filter((s) => s.type === "PHYSIQUE");
        if (form.locationType === "VIRTUELLE") return salles.filter((s) => s.type === "VIRTUELLE");
        return [];
    }, [salles, form.locationType]);

    // ── Sessions ───────────────────────────────────────────────────────────
    const handleSeancesCountChange = (value, existingSessions = []) => {
        setSeancesCount(value);
        const arr = [];
        for (let i = 1; i <= value; i++) {
            const ex = existingSessions[i - 1];
            arr.push({
                sorder:     ex ? ex.sorder     : i,
                name:       ex ? ex.name       : `Séance ${i}`,
                dureeHours: ex ? pf(ex.dureeHours) : 1,
                startat:    ex ? ex.startat    : null,
                endat:      ex ? ex.endat      : null,
                id:         ex ? ex.id         : null,
            });
        }
        setSeances(arr);
    };

    // ── Ouvrir / fermer modales ────────────────────────────────────────────
    const openAdd = () => {
        setOpen(true); setIsEdit(false); setCurrentId(null);
        setModalTab("INFOS"); setForm(emptyForm);
        setConsultSearch(""); setSelectedConsultantIds(new Set());
        setSeancesCount(0); setSeances([]);
        setBudgetType("formation"); setBudget(emptyBudget());
        setSelectedEvaluationIds(new Set());
        setEvaluationSearch("");
    };

    const openEdit = (e) => {
        setOpen(true); setIsEdit(true); setCurrentId(e.id);
        setModalTab("INFOS"); setConsultSearch("");
        setForm({
            ...emptyForm,
            titre:                     e.titre || "",
            description:               e.description || "",
            moduleCleId:               String((e.moduleCle && e.moduleCle.id) || ""),
            locationType:              e.locationType || "ONSITE",
            salleId:                   String((e.salle && e.salle.id) || ""),
            offsiteLocation:           e.offsiteLocation || "",
            dateDebut:                 toIsoLocal(e.dateDebut),
            dateFin:                   toIsoLocal(e.dateFin),
            allowRegistration:         e.allowRegistration !== false,
            registrationDeadlineHours: e.registrationDeadlineHours != null ? e.registrationDeadlineHours : "",
            isPeriodic:                !!e.isPeriodic,
            maxPlaces:                 e.maxPlaces != null ? e.maxPlaces : "",
            currency:                  e.currency || "MAD",
        });
        setBudget({
            tarifFormation:   pf(e.tarifFormation),
            repas:            pf(e.repas),
            transport:        pf(e.transport),
            hebergement:      pf(e.hebergement),
            salleConference:  pf(e.salleConference),
            autres:           pf(e.autres),
            autreDescription: e.autreDescription || "",
        });
        setSelectedConsultantIds(new Set((e.consultants || []).map((c) => c.id)));
        setSelectedEvaluationIds(
            new Set((e.evaluations || []).map((ev) => ev.id))
        );
        const existingSessions = e.sessions || [];
        handleSeancesCountChange(existingSessions.length, existingSessions);
        if (e.moduleCle && e.moduleCle.campagne && e.moduleCle.campagne.id) {
            dispatch(fetchBudgetByCampagneRequest(e.moduleCle.campagne.id));
        }
    };

    const close = () => {
        setOpen(false); setForm(emptyForm);
        setSelectedConsultantIds(new Set()); setConsultSearch("");
        setSeancesCount(0); setSeances([]);
        setBudget(emptyBudget());
        setSelectedEvaluationIds(new Set());
        setEvaluationSearch("");
    };

    const toggleConsultant = (id) => {
        setSelectedConsultantIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleEvaluation = (id) => {
        setSelectedEvaluationIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const filteredEvaluations = useMemo(() => {
        const s = String(evaluationSearch || "").trim().toLowerCase();
        return evaluations.filter((ev) => {
            if (!s) return true;
            return (
                String(ev.code || "").toLowerCase().includes(s) ||
                String(ev.name || "").toLowerCase().includes(s) ||
                String(ev.typeCode || "").toLowerCase().includes(s)
            );
        });
    }, [evaluations, evaluationSearch]);
    // ── Validation + payload ───────────────────────────────────────────────
    const validate = () => {
        if (!form.titre.trim())               return "Titre obligatoire";
        if (!form.moduleCleId)                return "Module obligatoire";
        if (!form.dateDebut || !form.dateFin) return "Dates obligatoires";
        if (form.locationType === "OFFSITE" && !form.offsiteLocation.trim())
            return "Lieu OFFSITE obligatoire";
        if (form.locationType !== "OFFSITE" && !form.salleId)
            return "Salle obligatoire (ONSITE/VIRTUELLE)";
        return null;
    };

    const buildPayload = () => {
        const coutTotal = pf(budget.tarifFormation) + pf(budget.repas) + pf(budget.transport)
            + pf(budget.hebergement) + pf(budget.salleConference) + pf(budget.autres);

        return {
            titre:                     form.titre.trim(),
            description:               form.description ? form.description.trim() : null,
            moduleCleId:               Number(form.moduleCleId),
            locationType:              form.locationType,
            salleId:                   form.locationType === "OFFSITE" ? null : Number(form.salleId),
            offsiteLocation:           form.locationType === "OFFSITE" ? form.offsiteLocation.trim() : null,
            dateDebut:                 form.dateDebut ? new Date(form.dateDebut).toISOString() : null,
            dateFin:                   form.dateFin   ? new Date(form.dateFin).toISOString()   : null,
            allowRegistration:         !!form.allowRegistration,
            registrationDeadlineHours: form.allowRegistration ? toIntOrNull(form.registrationDeadlineHours) : null,
            isPeriodic:                !!form.isPeriodic,
            maxPlaces:                 toIntOrNull(form.maxPlaces),
            currency:                  form.currency || "MAD",
            tarifFormation:            toDecimalOrNull(budget.tarifFormation),
            repas:                     toDecimalOrNull(budget.repas),
            transport:                 toDecimalOrNull(budget.transport),
            hebergement:               toDecimalOrNull(budget.hebergement),
            salleConference:           toDecimalOrNull(budget.salleConference),
            autres:                    toDecimalOrNull(budget.autres),
            autreDescription:          budget.autreDescription || null,
            cout:                      toDecimalOrNull(coutTotal),
            budgetRestant:             toDecimalOrNull(budgetRestantCalc),
            consultantIds:             Array.from(selectedConsultantIds),
            evaluationIds: Array.from(selectedEvaluationIds),
            sessions: seances.map((s) => ({
                id:         s.id || null,
                sorder:     s.sorder,
                name:       s.name,
                dureeHours: s.dureeHours,
                startat:    s.startat ? new Date(s.startat).toISOString() : null,
                endat:      s.endat   ? new Date(s.endat).toISOString()   : null,
            })),
        };
    };

    const submit = () => {
        const err = validate();
        if (err) { Swal.fire({ icon: "warning", text: err, timer: 2000, showConfirmButton: false }); return; }
        const payload = buildPayload();
        if (isEdit && currentId) dispatch(updateEventRequest(currentId, payload));
        else                     dispatch(createEventRequest(payload));
        close();
    };

    // ── Actions tableau ────────────────────────────────────────────────────
    const onDelete = (id) => Swal.fire({
        title: "Supprimer ?", text: "Action irréversible", icon: "warning",
        showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Annuler",
        confirmButtonColor: "#ee645f", cancelButtonColor: "#6b7280",
    }).then((r) => { if (r.isConfirmed) dispatch(deleteEventRequest(id)); });

    const onCancel = (id) => Swal.fire({
        title: "Annuler l'événement ?", text: "Le coût sera restitué au budget campagne.",
        icon: "warning", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non",
        confirmButtonColor: "#ee645f", cancelButtonColor: "#6b7280",
    }).then((r) => { if (r.isConfirmed) dispatch(cancelEventRequest(id)); });

    const onDuplicate = (id) => Swal.fire({
        title: "Dupliquer ?", text: "Copie créée sans participants.", icon: "question",
        showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non",
        confirmButtonColor: "#204385", cancelButtonColor: "#6b7280",
    }).then((r) => { if (r.isConfirmed) dispatch(duplicateEventRequest(id)); });

    const openReplan = (ev) => {
        setReplanEventId(ev.id);
        setReplanForm({ dateDebut: toIsoLocal(ev.dateDebut), dateFin: toIsoLocal(ev.dateFin) });
        setReplanOpen(true);
    };

    const submitReplan = () => {
        if (!replanForm.dateDebut || !replanForm.dateFin) {
            Swal.fire({ icon: "warning", text: "Dates obligatoires", timer: 1800, showConfirmButton: false });
            return;
        }
        dispatch(replanifyEventRequest({
            id:        replanEventId,
            dateDebut: new Date(replanForm.dateDebut).toISOString(),
            dateFin:   new Date(replanForm.dateFin).toISOString(),
        }));
        setReplanOpen(false);
    };

    // ─────────────────────────────────────────────────────────────────────
    // RENDU
    // ─────────────────────────────────────────────────────────────────────
    return (
        <div className="evPage">

            {/* ── En-tête ──────────────────────────────────────────────── */}
            <div className="evHeader">
                <div>
                    <h2>Événements</h2>
                    <p className="evSub">Gérez et planifiez les événements de formation</p>
                </div>
                <div className="evHeaderBtns">
                    <button className="evBtn evBtnLight" onClick={() => dispatch(exportBudgetRequest())}>
                        <Download size={14} /> Budget Excel
                    </button>
                    <button className="evBtn evBtnLight" onClick={() => dispatch(exportFormationsRequest())}>
                        <FileSpreadsheet size={14} /> Formations Excel
                    </button>
                    <button className="evBtn evBtnPrimary" onClick={openAdd}>
                        + Ajouter événement
                    </button>
                </div>
            </div>
            {/* ── SECTION IMPORT ──────────────────────────────────────────────────── */}
            <div className="evImportSection">
                <div className="evImportHeader">
                    <span>Import Excel</span>
                    <div className="evImportHeaderActions">
                        <button
                            className="evBtn evBtnLight"
                            onClick={() => dispatch(downloadEventsTemplateRequest())}
                            disabled={loading}
                            title="Télécharger le template"
                        >
                            <FileSpreadsheet size={14} /> Template
                        </button>
                        <button
                            className="evBtn evBtnLight"
                            onClick={() => dispatch(exportEventsExcelRequest())}
                            disabled={loading}
                            title="Exporter tous les événements"
                        >
                            <Download size={14} /> Exporter
                        </button>
                    </div>
                </div>

                <div className="evImportRow">
                    <label className={`evFileLabel ${fileName ? "has-file" : ""}`}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                        />
                        <Upload size={14} />
                        <span>{fileName || "Choisir un fichier .xlsx"}</span>
                    </label>

                    {fileName && (
                        <button className="evIconBtn" onClick={clearFile} title="Retirer">
                            <X size={13} />
                        </button>
                    )}

                    <select
                        className="evSelect"
                        value={importAction}
                        onChange={(e) => setImportAction(e.target.value)}
                    >
                        <option value="add">Ajout</option>
                        <option value="edit">Mise à jour</option>
                    </select>

                    <div style={{ flex: 1 }} />

                    <button
                        className="evBtn evBtnLight"
                        onClick={handleVerify}
                        disabled={loading || !selectedFile}
                    >
                        <Eye size={14} /> {loading ? "Analyse..." : "Vérifier"}
                    </button>

                    <button
                        className="evBtn evBtnPrimary"
                        onClick={handleConfirmImport}
                        disabled={loading || !selectedFile}
                    >
                        <Upload size={14} /> Importer direct
                    </button>
                </div>
            </div>

            {/* ── MODALE PREVIEW DRY RUN ──────────────────────────────────────────── */}
            {isPreviewOpen && importResult && (
                <div className="evModal" role="dialog" aria-modal="true" onMouseDown={() => setIsPreviewOpen(false)}>
                    <div className="evModalPanel evModalWide" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="evModalHead">
                            <h3>Prévisualisation import</h3>
                            <button className="evIconBtn" onClick={() => setIsPreviewOpen(false)}>✕</button>
                        </div>

                        {/* Stats */}
                        <div className="evPreviewStats">
                            <div className="evStatOk">
                                <CheckCircle size={16} />
                                <span><strong>{importResult.createdCount}</strong> ligne{importResult.createdCount !== 1 ? "s" : ""} valide{importResult.createdCount !== 1 ? "s" : ""}</span>
                            </div>
                            <div className="evStatErr">
                                <X size={16} />
                                <span><strong>{importResult.skippedCount}</strong> ignorée{importResult.skippedCount !== 1 ? "s" : ""}</span>
                            </div>
                        </div>

                        {/* Erreurs */}
                        {importResult.errors && importResult.errors.length > 0 && (
                            <div className="evPreviewErrors">
                                <p className="evSectionTitle">Erreurs détectées</p>
                                {importResult.errors.map((err, i) => (
                                    <div key={i} className="evErrorItem">
                                        <span className="evErrRow">Ligne {err.row}</span>
                                        <span className="evErrField">{err.field}</span>
                                        <span className="evErrMsg">{err.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Aperçu lignes valides */}
                        {importResult.preview && importResult.preview.length > 0 && (
                            <div className="evPreviewTableWrap">
                                <p className="evSectionTitle">Lignes qui seront importées</p>
                                <table className="evTable evTableSm">
                                    <thead>
                                    <tr>
                                        <th>Réf.</th>
                                        <th>Titre</th>
                                        <th>Module</th>
                                        <th>Campagne</th>
                                        <th>Début</th>
                                        <th>Fin</th>
                                        <th>Type lieu</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {importResult.preview.map((row, i) => (
                                        <tr key={i}>
                                            <td><span className="evCode">{row.reference || "—"}</span></td>
                                            <td>{row.titre}</td>
                                            <td>{row.moduleCleNom || "—"}</td>
                                            <td>{row.campagneReference || "—"}</td>
                                            <td>{row.dateDebut ? String(row.dateDebut).slice(0, 16).replace("T", " ") : "—"}</td>
                                            <td>{row.dateFin   ? String(row.dateFin).slice(0,   16).replace("T", " ") : "—"}</td>
                                            <td>{row.locationType || "—"}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="evModalFoot">
                            <button className="evBtn evBtnLight" onClick={() => setIsPreviewOpen(false)}>Annuler</button>
                            <button
                                className="evBtn evBtnPrimary"
                                onClick={handleConfirmImport}
                                disabled={loading || importResult.createdCount === 0}
                            >
                                {loading ? "Import en cours..." : `Confirmer (${importResult.createdCount} ligne${importResult.createdCount !== 1 ? "s" : ""})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ── Toolbar ──────────────────────────────────────────────── */}
            <div className="evTabs">
                {[
                    { key: "ALL",       label: "Tous" },
                    { key: "ONSITE",    label: "Onsite" },
                    { key: "OFFSITE",   label: "Offsite" },
                    { key: "VIRTUELLE", label: "Virtuelle" },
                ].map((t) => (
                    <button key={t.key} className={`evTab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
                        {t.label}
                    </button>
                ))}
                <div className="evSpacer" />
                <input className="evSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un événement…" />
            </div>

            {/* ── Tableau ──────────────────────────────────────────────── */}
            <div className="evCard">
                <div className="evTableWrap">
                    {loading && <div className="evLoading"><div className="evSpinner" /> Chargement…</div>}
                    <table className="evTable">
                        <thead>
                        <tr>
                            <th>Titre</th>
                            <th>Statut</th>
                            <th>Lieu</th>
                            <th>Module</th>
                            <th>Début</th>
                            <th>Fin</th>
                            <th>Évaluations</th>

                            <th>Séances</th>
                            <th style={{ width: 220, textAlign: "right" }}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filteredEvents.length === 0 ? (
                            <tr>
                                <td colSpan={8}>
                                    <div className="evEmpty">Aucun événement trouvé</div>
                                </td>
                            </tr>
                        ) : filteredEvents.map((e) => {
                            const sc = statusColor(e.status);
                            return (
                                <tr key={e.id} style={{ opacity: e.status === "ANNULE" ? 0.55 : 1 }}>
                                    <td style={{ fontWeight: 700, color: "#204385", fontSize: 12 }}>{e.titre}</td>
                                    <td>
                                            <span className="evBadge" style={{ background: sc.bg, color: sc.color, borderColor: sc.bg }}>
                                                {e.status || "—"}
                                            </span>
                                    </td>
                                    <td>
                                        <span className="evBadge" style={{ marginRight: 6 }}>{e.locationType}</span>
                                        <span style={{ fontSize: 11, color: "#6b7280" }}>
                                                {e.locationType === "OFFSITE"
                                                    ? (e.offsiteLocation || "—")
                                                    : (e.salle && e.salle.nom) || "—"}
                                            </span>
                                    </td>
                                    <td>
    <span className="evBadge">
        {(e.evaluations || []).length}
    </span>
                                    </td>
                                    <td style={{ fontSize: 11, color: "#6b7280" }}>{(e.moduleCle && e.moduleCle.nom) || "—"}</td>
                                    <td style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                                        {String(e.dateDebut || "").replace("T", " ").slice(0, 16)}
                                    </td>
                                    <td style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}>
                                        {String(e.dateFin || "").replace("T", " ").slice(0, 16)}
                                    </td>
                                    <td>
                                        <span className="evBadge">{(e.sessions || []).length}</span>
                                    </td>
                                    <td className="evActions">
                                        <button className="evBtn evBtnLight" onClick={() => openEdit(e)} disabled={e.status === "ANNULE"} title="Modifier" style={{ padding: "6px 8px" }}>
                                            <Pencil size={14} />
                                        </button>
                                        <button className="evBtn evBtnLight" onClick={() => openReplan(e)} disabled={e.status === "ANNULE"} title="Réplanifier" style={{ padding: "6px 8px" }}>
                                            <CalendarClock size={14} />
                                        </button>
                                        <button className="evBtn evBtnLight" onClick={() => onDuplicate(e.id)} title="Dupliquer" style={{ padding: "6px 8px" }}>
                                            <Copy size={14} />
                                        </button>
                                        <button className="evBtn evBtnWarning" onClick={() => onCancel(e.id)} disabled={e.status === "ANNULE"} title="Annuler" style={{ padding: "6px 8px" }}>
                                            <Ban size={14} />
                                        </button>
                                        <button className="evBtn evBtnDanger" onClick={() => onDelete(e.id)} title="Supprimer" style={{ padding: "6px 8px" }}>
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── MODALE CRÉER / MODIFIER ──────────────────────────────── */}
            {open && (
                <div className="evModal" role="dialog" aria-modal="true" onMouseDown={close}>
                    <div className="evModalPanel evModalLarge" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="evModalHead">
                            <h3>{isEdit ? "Modifier l'événement" : "Ajouter un événement"}</h3>
                            <button className="evIconBtn" onClick={close}>✕</button>
                        </div>

                        {/* Onglets modale */}
                        <div className="evModalTabs">
                            {["INFOS", "SESSIONS", "CONSULTANTS","EVALUATIONS", "BUDGET"].map((t) => (
                                <button key={t} className={`evModalTab ${modalTab === t ? "active" : ""}`} onClick={() => setModalTab(t)}>
                                    {t === "SESSIONS"    ? `Séances (${seancesCount})`
                                        : t === "CONSULTANTS" ? `Consultants (${selectedConsultantIds.size})`
                                            : t.charAt(0) + t.slice(1).toLowerCase()}
                                </button>
                            ))}
                        </div>

                        {modalTab === "EVALUATIONS" && (
                            <div className="evBox">
                                <p className="evSectionTitle">Sélection des évaluations</p>

                                <div className="evBoxTop">
                                    <input
                                        className="evSearch"
                                        placeholder="Rechercher une évaluation…"
                                        value={evaluationSearch}
                                        onChange={(e) => setEvaluationSearch(e.target.value)}
                                    />

                                    <div className="evSmallBtns">
                                        <button
                                            className="evBtn evBtnLight"
                                            onClick={() =>
                                                setSelectedEvaluationIds((prev) => {
                                                    const next = new Set(prev);
                                                    filteredEvaluations.forEach((ev) => next.add(ev.id));
                                                    return next;
                                                })
                                            }
                                        >
                                            Tout sélectionner
                                        </button>

                                        <button
                                            className="evBtn evBtnLight"
                                            onClick={() => setSelectedEvaluationIds(new Set())}
                                        >
                                            Tout désélectionner
                                        </button>
                                    </div>
                                </div>

                                <div className="evList">
                                    {filteredEvaluations.length === 0 ? (
                                        <div className="evEmpty">Aucune évaluation trouvée</div>
                                    ) : (
                                        filteredEvaluations.map((ev) => (
                                            <label key={ev.id} className="evRow">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEvaluationIds.has(ev.id)}
                                                    onChange={() => toggleEvaluation(ev.id)}
                                                />
                                                <div className="evRowInfo">
                                                    <div className="evRowName">
                                                        {ev.name}
                                                        {ev.code && (
                                                            <span className="evMuted"> • {ev.code}</span>
                                                        )}
                                                    </div>
                                                    <div className="evMuted">
                                                        {ev.typeCode || "—"}
                                                    </div>
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ── INFOS ──────────────────────────────────────── */}
                        {modalTab === "INFOS" && (
                            <div className="evGrid">
                                <div className="evField">
                                    <label>Titre *</label>
                                    <input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Ex : Formation Excel avancé" />
                                </div>

                                <div className="evField">
                                    <label>Module *</label>
                                    <select
                                        value={form.moduleCleId}
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            setForm({ ...form, moduleCleId: id });
                                            const mod = modulesCle.find((m) => String(m.id) === id);
                                            if (mod && mod.campagneId) {
                                                dispatch(fetchBudgetByCampagneRequest(mod.campagneId));
                                            }
                                        }}
                                    >
                                        <option value="">— Choisir —</option>
                                        {modulesCle.map((m) => (
                                            <option key={m.id} value={String(m.id)}>
                                                {m.reference ? `${m.reference} - ` : ""}{m.nom}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="evField">
                                    <label>Type de lieu *</label>
                                    <select
                                        value={form.locationType}
                                        onChange={(e) => setForm({ ...form, locationType: e.target.value, salleId: "", offsiteLocation: "" })}
                                    >
                                        <option value="ONSITE">On-site</option>
                                        <option value="OFFSITE">Off-site</option>
                                        <option value="VIRTUELLE">Virtuelle</option>
                                    </select>
                                </div>

                                {(form.locationType === "ONSITE" || form.locationType === "VIRTUELLE") && (
                                    <div className="evField">
                                        <label>{form.locationType === "VIRTUELLE" ? "Salle virtuelle *" : "Salle *"}</label>
                                        <select value={form.salleId} onChange={(e) => setForm({ ...form, salleId: e.target.value })}>
                                            <option value="">— Choisir —</option>
                                            {sallesFiltrees.map((s) => (
                                                <option key={s.id} value={String(s.id)}>{s.nom}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {form.locationType === "OFFSITE" && (
                                    <div className="evField evFull">
                                        <label>Lieu exact *</label>
                                        <input
                                            value={form.offsiteLocation}
                                            onChange={(e) => setForm({ ...form, offsiteLocation: e.target.value })}
                                            placeholder="Ex: Hôtel Kenzi Tower, Casablanca"
                                        />
                                    </div>
                                )}

                                <div className="evField">
                                    <label>Date début *</label>
                                    <input type="datetime-local" value={form.dateDebut} onChange={(e) => setForm({ ...form, dateDebut: e.target.value })} />
                                </div>
                                <div className="evField">
                                    <label>Date fin *</label>
                                    <input type="datetime-local" value={form.dateFin} onChange={(e) => setForm({ ...form, dateFin: e.target.value })} />
                                </div>

                                <div className="evField evFull">
                                    <label>Description</label>
                                    <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description de l'événement…" />
                                </div>

                                <div className="evField">
                                    <label>Inscription autorisée</label>
                                    <select value={form.allowRegistration ? "1" : "0"} onChange={(e) => setForm({ ...form, allowRegistration: e.target.value === "1" })}>
                                        <option value="1">Oui</option>
                                        <option value="0">Non</option>
                                    </select>
                                </div>
                                <div className="evField">
                                    <label>Délai inscription (heures)</label>
                                    <input
                                        disabled={!form.allowRegistration}
                                        placeholder="Ex: 24"
                                        value={form.registrationDeadlineHours}
                                        onChange={(e) => setForm({ ...form, registrationDeadlineHours: e.target.value })}
                                    />
                                </div>
                                <div className="evField">
                                    <label>Périodique</label>
                                    <select value={form.isPeriodic ? "1" : "0"} onChange={(e) => setForm({ ...form, isPeriodic: e.target.value === "1" })}>
                                        <option value="0">Non</option>
                                        <option value="1">Oui</option>
                                    </select>
                                </div>
                                <div className="evField">
                                    <label>Max places</label>
                                    <input placeholder="Ex: 20" value={form.maxPlaces} onChange={(e) => setForm({ ...form, maxPlaces: e.target.value })} />
                                </div>
                                <div className="evField">
                                    <label>Devise</label>
                                    <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
                                </div>
                            </div>
                        )}

                        {/* ── SESSIONS ───────────────────────────────────── */}
                        {modalTab === "SESSIONS" && (
                            <div className="evSeancesWrapper">
                                <p className="evSectionTitle">Configuration des séances</p>

                                {/* Nombre de séances */}
                                <div className="evSeancesCount">
                                    <div className="evField">
                                        <label>Nombre de séances</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={30}
                                            value={seancesCount}
                                            onChange={(e) => handleSeancesCountChange(Number(e.target.value), seances)}
                                        />
                                    </div>
                                </div>

                                {/* Table des séances */}
                                {seancesCount > 0 ? (
                                    <div className="evSeanceTableWrap">
                                        <table className="evSeanceTable">
                                            <thead>
                                            <tr>
                                                <th style={{ width: 48 }}>#</th>
                                                <th>Nom de la séance</th>
                                                <th style={{ width: 130 }}>Durée (h)</th>
                                                <th>Date début</th>
                                                <th>Date fin</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {[...seances]
                                                .sort((a, b) => a.sorder - b.sorder)
                                                .map((seance) => (
                                                    <tr key={seance.sorder}>
                                                        <td>
                                                            <span className="evSeanceNum">{seance.sorder}</span>
                                                        </td>
                                                        <td>
                                                            <div className="evSeanceNameCell">
                                                                <span className="evSeanceNameText">{seance.name}</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                min={0.5}
                                                                max={24}
                                                                step={0.5}
                                                                className="evSeanceInput"
                                                                value={seance.dureeHours}
                                                                onChange={(e) =>
                                                                    setSeances(seances.map((s) =>
                                                                        s.sorder === seance.sorder
                                                                            ? { ...s, dureeHours: parseFloat(e.target.value) || 0 }
                                                                            : s
                                                                    ))
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="datetime-local"
                                                                className="evSeanceInput"
                                                                value={toIsoLocal(seance.startat)}
                                                                onChange={(e) =>
                                                                    setSeances(seances.map((s) =>
                                                                        s.sorder === seance.sorder
                                                                            ? { ...s, startat: e.target.value }
                                                                            : s
                                                                    ))
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="datetime-local"
                                                                className="evSeanceInput"
                                                                value={toIsoLocal(seance.endat)}
                                                                onChange={(e) =>
                                                                    setSeances(seances.map((s) =>
                                                                        s.sorder === seance.sorder
                                                                            ? { ...s, endat: e.target.value }
                                                                            : s
                                                                    ))
                                                                }
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="evSeancesEmpty">
                                        Saisissez un nombre de séances pour commencer
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── CONSULTANTS ────────────────────────────────── */}
                        {modalTab === "CONSULTANTS" && (
                            <div className="evBox">
                                <p className="evSectionTitle">Sélection des consultants</p>
                                <div className="evBoxTop">
                                    <input className="evSearch" placeholder="Rechercher consultant…" value={consultSearch} onChange={(e) => setConsultSearch(e.target.value)} />
                                    <div className="evSmallBtns">
                                        <button className="evBtn evBtnLight" onClick={() => setSelectedConsultantIds((p) => { const n = new Set(p); filteredConsultants.forEach((c) => n.add(c.id)); return n; })}>
                                            Tout sélectionner
                                        </button>
                                        <button className="evBtn evBtnLight" onClick={() => setSelectedConsultantIds(new Set())}>
                                            Tout désélectionner
                                        </button>
                                    </div>
                                </div>
                                <div className="evList">
                                    {filteredConsultants.length === 0 ? (
                                        <div className="evEmpty">Aucun consultant trouvé</div>
                                    ) : filteredConsultants.map((c) => (
                                        <label key={c.id} className="evRow">
                                            <input type="checkbox" checked={selectedConsultantIds.has(c.id)} onChange={() => toggleConsultant(c.id)} />
                                            <div className="evRowInfo">
                                                <div className="evRowName">
                                                    {c.nomContact}
                                                    {c.emailContact && <span className="evMuted"> • {c.emailContact}</span>}
                                                </div>
                                                <div className="evMuted">{c.specialite || "—"}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ── BUDGET ─────────────────────────────────────── */}
                        {modalTab === "BUDGET" && (
                            <div className="evBudgetWrapper">
                                <p className="evSectionTitle">Répartition du budget</p>

                                <div className="evBudgetTabs">
                                    <label className="evBudgetRadio">
                                        <input type="radio" name="budgetType" value="formation" checked={budgetType === "formation"} onChange={() => setBudgetType("formation")} />
                                        Formation
                                    </label>
                                    <label className="evBudgetRadio">
                                        <input type="radio" name="budgetType" value="logistique" checked={budgetType === "logistique"} onChange={() => setBudgetType("logistique")} />
                                        Logistique
                                    </label>
                                </div>

                                <div className="evBudgetGrid">
                                    {budgetType === "formation" && (
                                        <div className="evField">
                                            <label>Tarif formation (MAD)</label>
                                            <input type="number" min={0} step={0.01} value={budget.tarifFormation} onChange={(e) => setBudget({ ...budget, tarifFormation: e.target.value })} />
                                        </div>
                                    )}

                                    {budgetType === "logistique" && (
                                        <>
                                            {[
                                                ["repas",           "Repas"],
                                                ["hebergement",     "Hébergement"],
                                                ["transport",       "Transport"],
                                                ["salleConference", "Salle conférence"],
                                                ["autres",          "Autres charges"],
                                            ].map(([key, label]) => (
                                                <div className="evField" key={key}>
                                                    <label>{label} (MAD)</label>
                                                    <input type="number" min={0} step={0.01} value={budget[key]} onChange={(e) => setBudget({ ...budget, [key]: e.target.value })} />
                                                </div>
                                            ))}
                                            <div className="evField evFull">
                                                <label>Description autres charges</label>
                                                <input value={budget.autreDescription} onChange={(e) => setBudget({ ...budget, autreDescription: e.target.value })} />
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="evBudgetTotaux">
                                    <div className="evBudgetTotal">
                                        <span>Total saisi</span>
                                        <strong>{totalCout.toFixed(2)} {form.currency}</strong>
                                    </div>
                                    <div className="evBudgetCampagne">
                                        <span>Budget campagne</span>
                                        <strong className="green">{montantCampagne.toFixed(2)} MAD</strong>
                                    </div>
                                    <div className="evBudgetRestant">
                                        <span>Budget restant après événement</span>
                                        <strong className={budgetRestantCalc < 0 ? "red" : "green"}>
                                            {budgetRestantCalc.toFixed(2)} MAD
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="evModalFoot">
                            <button className="evBtn evBtnLight" onClick={close}>Annuler</button>
                            <button className="evBtn evBtnPrimary" onClick={submit}>{isEdit ? "Enregistrer" : "Créer"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── MODALE RÉPLANIFICATION ────────────────────────────────── */}
            {replanOpen && (
                <div className="evModal" role="dialog" aria-modal="true" onMouseDown={() => setReplanOpen(false)}>
                    <div className="evModalPanel evModalSmall" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="evModalHead">
                            <h3>Réplanifier l'événement</h3>
                            <button className="evIconBtn" onClick={() => setReplanOpen(false)}>✕</button>
                        </div>
                        <div className="evGrid" style={{ maxHeight: "none" }}>
                            <div className="evField">
                                <label>Nouvelle date début *</label>
                                <input type="datetime-local" value={replanForm.dateDebut} onChange={(e) => setReplanForm({ ...replanForm, dateDebut: e.target.value })} />
                            </div>
                            <div className="evField">
                                <label>Nouvelle date fin *</label>
                                <input type="datetime-local" value={replanForm.dateFin} onChange={(e) => setReplanForm({ ...replanForm, dateFin: e.target.value })} />
                            </div>
                            <p className="evHint evFull">Les heures seront forcées à <strong>09:00 → 17:00</strong> et les séances régénérées automatiquement.</p>
                        </div>
                        <div className="evModalFoot">
                            <button className="evBtn evBtnLight" onClick={() => setReplanOpen(false)}>Annuler</button>
                            <button className="evBtn evBtnPrimary" onClick={submitReplan}>Réplanifier</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}